import { useEffect, useRef, useState } from "react";
import LogManager from "../utils/logManager";

/**
 * usePublishedContentStore — manages UX Publish content keyed by (extensionId, contentId).
 *
 * Supports upsert (with updatedAt dedup), remove (with fade-out grace), TTL timers,
 * and bulk load from catch-up API.
 */
export function usePublishedContentStore() {
  const [contents, setContents] = useState(new Map());
  // Cards the user dismissed, keyed by "<scope>#<segmentIndex>". Held here so a
  // dismissal survives a tab switch (which unmounts the assistant tree) and resets
  // with the session via clear().
  const [dismissedCards, setDismissedCards] = useState(new Set());
  const timersRef = useRef(new Map());

  const makeKey = (extensionId, contentId) => `${extensionId}#${contentId}`;

  const clearTimer = (key) => {
    const existing = timersRef.current.get(key);
    if (existing) {
      clearTimeout(existing);
      timersRef.current.delete(key);
    }
  };

  const startTtlTimer = (key, ttlSeconds) => {
    clearTimer(key);
    const timerId = setTimeout(() => {
      timersRef.current.delete(key);
      setContents((prev) => {
        const next = new Map(prev);
        next.delete(key);
        return next;
      });
      LogManager.info(`[PublishedContent] TTL expired for ${key}`);
    }, ttlSeconds * 1000);
    timersRef.current.set(key, timerId);
  };

  const upsert = (item) => {
    const { extensionId, contentId, updatedAt } = item;
    if (!extensionId || !contentId) return;

    const key = makeKey(extensionId, contentId);

    setContents((prev) => {
      const existing = prev.get(key);
      if (existing && existing.updatedAt >= updatedAt) {
        return prev;
      }
      const next = new Map(prev);
      next.set(key, { ...item, key });
      return next;
    });

    if (item.ttlSeconds) {
      startTtlTimer(key, item.ttlSeconds);
    } else {
      clearTimer(key);
    }
  };

  const remove = (extensionId, contentId, updatedAt) => {
    const key = makeKey(extensionId, contentId);

    setContents((prev) => {
      const existing = prev.get(key);
      if (!existing) return prev;
      if (updatedAt && existing.updatedAt > updatedAt) return prev;
      const next = new Map(prev);
      next.delete(key);
      return next;
    });

    clearTimer(key);
  };

  const loadCatchUp = (items) => {
    if (!items || !items.length) return;

    setContents((prev) => {
      const next = new Map(prev);
      for (const item of items) {
        const { extensionId, contentId, updatedAt } = item;
        if (!extensionId || !contentId) continue;
        const key = makeKey(extensionId, contentId);
        const existing = next.get(key);
        if (existing && existing.updatedAt >= updatedAt) continue;
        next.set(key, { ...item, key });
      }
      return next;
    });

    for (const item of items) {
      if (!item.extensionId || !item.contentId) continue;
      const key = makeKey(item.extensionId, item.contentId);
      // Re-arm unconditionally — a remount drops pending timers. Backend already
      // decremented ttlSeconds for elapsed time.
      if (item.ttlSeconds > 0) {
        startTtlTimer(key, item.ttlSeconds);
      }
    }

    LogManager.info(`[PublishedContent] Loaded ${items.length} items from catch-up`);
  };

  const dismissCard = (cardKey) => {
    setDismissedCards((prev) => {
      if (prev.has(cardKey)) return prev;
      const next = new Set(prev);
      next.add(cardKey);
      return next;
    });
  };

  const clear = () => {
    for (const timerId of timersRef.current.values()) {
      clearTimeout(timerId);
    }
    timersRef.current.clear();
    setContents(new Map());
    setDismissedCards(new Set());
  };

  useEffect(() => {
    return () => {
      for (const timerId of timersRef.current.values()) {
        clearTimeout(timerId);
      }
    };
  }, []);

  const contentList = Array.from(contents.values())
    .sort((a, b) => (a.publishedAt || a.updatedAt) - (b.publishedAt || b.updatedAt));

  return { contents, contentList, upsert, remove, loadCatchUp, clear, dismissedCards, dismissCard };
}
